import type { Strings } from './types'

// English dictionary. Values are byte-identical to the pre-i18n literals —
// changing them changes the (control) English experience.
export const en: Strings = {
  hero: {
    methodologyLink: 'Read the methodology →',
    badgesDefault: ['Mifflin-St Jeor', 'Katch-McArdle', 'Ainsworth 2011 MET', 'No account required'],
    badgesGlp1: ['Protein target', 'Muscle preservation', 'Safe calorie floor', 'No account required'],
  },
  disclaimer: {
    text: 'Not medical advice · Estimates, track 2 weeks to calibrate · Free, no account required',
    methodology: 'Methodology',
  },
  error: {
    headline: "Hmm, that didn't go through.",
    busy: 'The AI service is briefly busy — try sending your message again in a few seconds. Your conversation is saved.',
  },
  chat: {
    hintKicker: 'AI interview · Claude',
    hintMeta: '~60 seconds · 5–7 questions',
    steps: [
      { label: 'You & your body', detail: 'age, sex, height, weight' },
      { label: 'Occupation', detail: 'desk, active, on-your-feet' },
      { label: 'Daily movement', detail: 'step count, commute, household' },
      { label: 'Exercise', detail: 'what, how often, how hard' },
      { label: 'Diet style', detail: 'high-protein, low-carb, etc.' },
    ],
    exampleLead: 'Or skip ahead and type one line:',
    exampleDefault: '"32-year-old guy, 5\'10", 176 lb, desk job, about 6k steps a day."',
    exampleGlp1: '"On Zepbound, 34F, 5\'6", 190 lb — want to lose fat but keep my muscle."',
    mutedDefault: 'Metric works too (cm / kg).',
    mutedGlp1: 'Any GLP-1 works — Ozempic, Wegovy, Mounjaro, retatrutide. Metric too (cm / kg).',
    ariaStartDefault: 'Start your TDEE calculation',
    ariaStartGlp1: 'Start your GLP-1 calorie and protein calculation',
    startPrimaryDefault: 'Start my free TDEE calc',
    startPrimaryGlp1: 'Start my GLP-1 calc',
    startChipMaintain: 'Maintain my weight',
    startChipBuild: 'Build muscle',
    startChipRecomp: 'Recomp / build muscle',
    startChipComingOff: 'Maintain / coming off',
    seedLose: "I want to lose weight — let's start my TDEE calculation.",
    seedMaintain: "I want to maintain my weight — let's start my TDEE calculation.",
    seedBuild: "I want to build muscle — let's start my TDEE calculation.",
    seedGlp1Start:
      "I'm on a GLP-1 and want to lose fat while keeping my muscle — let's start my calorie & protein calculation.",
    seedGlp1Recomp:
      "I'm on a GLP-1 and lifting — I want to recomp (lose fat, build muscle). Let's start my calculation.",
    seedGlp1ComingOff:
      "I'm on a GLP-1 and want to maintain or come off it — let's start my calculation.",
    planOfferChip: 'Yes, make my plan',
    placeholderThinking: 'Thinking…',
    placeholderStart: `Start the interview — e.g. "32yo guy, 5'10", 176 lb, desk job"`,
    placeholderAnswer: 'Type your answer…',
    send: 'Send',
    srAnswer: 'Your answer',
  },
  blocked: {
    title: 'Access paused',
    body: "NanoRhino is built for adults, so we can't calculate calories or build a plan for anyone under 18. If you're 18 or older and reached this by mistake, email us and we'll restore access:",
    placeholder: 'Access paused — under 18',
  },
  result: {
    kicker: 'Your TDEE range',
    tierTight: 'Tight range',
    tierCalibrated: 'Calibrated',
    tierWide: 'Wide — track 2 weeks',
    unit: 'kcal/day',
    recommended: 'Recommended',
    showWork: 'Show your work',
    hide: 'Hide',
    show: 'Show',
    bmr: 'BMR',
    bmrMifflin: 'Mifflin-St Jeor: 10·kg + 6.25·cm − 5·yrs ± sex offset',
    bmrKatch: 'Katch-McArdle: 370 + 21.6 × lean body mass',
    neat: 'NEAT',
    neatHint: 'Daily non-exercise activity',
    occupation: 'Occupation',
    occupationHint: 'baseline band by role',
    steps: 'Steps',
    stepsHint: '~0.045 kcal × steps × (kg/70); replaces occupation if higher',
    commute: 'Commute',
    commuteHint: 'minutes × kcal/min by mode',
    household: 'Household',
    householdHint: 'task-hours × intensity band',
    tef: 'TEF',
    tefHint: 'thermic effect of food · diet-style adjusted',
    eat: 'EAT',
    eatHint: 'deliberate exercise',
    metCompendium: 'Ainsworth 2011 Compendium',
    perWk: '/wk',
    recalc: 'Recalculate',
    recalcTitle: 'Clear this conversation and start a new interview from scratch',
  },
  plan: {
    kicker: 'Your plan',
    building: 'Building…',
    errorPrefix: "Couldn't build your plan: ",
    unknownError: 'unknown error',
    tryAgain: 'Try again',
    skeleton: 'Personalizing your plan from your TDEE…',
  },
  cta: {
    kickerReady: 'Your plan is ready',
    kickerReadyGlp1: 'Your GLP-1 plan is ready',
    textMeBtn: 'Text me my plan →',
    fineprintUs: 'Free to text. No spam — a real coach answers.',
    emailInstead: 'Or email me the plan instead',
    convoCaption: 'What it looks like by text:',
    convoUser: '2 eggs, toast, black coffee',
    convoCoach: '~410 cal · 24g protein — logged. 86g protein to go today.',
    noAppLead: 'No app, no counting.',
    noAppBody: 'You text what you ate — a photo or a few words — and your coach does the math.',
    rangeLede: (tdee: string) =>
      `~${tdee} Cal/day is the formula's guess. Your real burn is somewhere in here:`,
    rangeLedeNoNumber:
      "The number you just got is the formula's guess — your real burn is somewhere around it.",
    rangeCaption: 'same stats, different bodies',
    rangeAria: (low: string, high: string, rec: string) =>
      `Your real daily burn is somewhere between ${low} and ${high} Calories; the formula picked ${rec}.`,
    rangeBody:
      "Two weeks of texted meals pins down yours — then your target is re-measured every Sunday from what you actually ate and weighed, so it can't quietly drift wrong.",
    ledeEstimate:
      'The number you just got is a formula estimate — a starting point, not a measurement.',
    bodySteady:
      'Your plan holds a daily target, a protein goal, and a few habits that keep the week steady.',
    glp1Lede:
      'Your medication handles the appetite — the blind spot is what you actually eat.',
    glp1Body:
      'Your coach measures your real intake and protein from your texts, so the weight you lose stays fat, not muscle.',
    convoPlanMeta: '~1 minute after you send',
    convoCaptionWeek: 'Your first week, by text:',
    convoSundayMeta: 'Sunday · weekly re-measure',
    convoCoachSunday:
      "Week 2 ✓ down 1.6 lb. You're burning above the formula, so this week's target goes up. Same pace, a bit more food.",
    previewTileProtein: 'Protein',
    previewTileMeals: 'Meals',
    intlKicker: 'Make it stick',
    intlLede:
      'The number you just got is a formula estimate — the same math every calculator runs. Your real TDEE can only be measured from your own logged meals and weigh-ins.',
    intlLedeSteady:
      'The number you just got is a formula estimate — the same math every calculator runs. What matters now is a plan you can actually live day to day.',
    whatsappBtn: 'Get my plan on WhatsApp →',
    whatsappMechanicHint: 'Opens WhatsApp with your plan request pre-filled — just hit send.',
    qrCaption: 'Scan with your phone — WhatsApp opens with your plan request ready to send.',
    qrDesktopLink: 'Have WhatsApp on this computer? Open it here',
    previewSender: 'NanoRhino',
    previewPlanLabel: 'Plan',
    titleGlp1: 'Your GLP-1 plan',
    titleMaintain: 'Your maintenance plan',
    titleGain: 'Your gain plan',
    titleRecomp: 'Your recomp plan',
    titleLose: 'Your weight loss plan',
    heroMaintainLabel: 'Daily maintenance target',
    heroDailyLabel: 'Starting daily target',
    heroGlp1PlanLabel: 'Your plan',
    heroGlp1Alt: 'Protein floor',
    heroGlp1Unit: 'hit it even on low-appetite days',
    heroNoneLabel: 'Starting daily target',
    heroNoneAlt: 'Your number',
    heroNoneUnit: 'Cal / day',
    heroActivityLabel: 'Eat steady at',
    honestPaceWithDate: (date: string) =>
      `Fastest pace that's safe for your body — ${date} is the honest date.`,
    honestPaceNoDate: "We set the fastest pace that's safe for your body.",
    ledeActivity:
      'For your body, the safe setup is a steady eating anchor plus movement that creates the deficit.',
    unitPerDay: 'Cal / day',
    smsPrefill: (tokenLower: string) => `Hi — send me my plan. I'm ${tokenLower}`,
    pitchKicker: 'AI coach + real nutritionist',
    pitchHeadline: 'No calculator can get your number exact.',
    pitchHeadlineLose: "Doing everything right and the scale won't move?",
    pitchSubLose:
      "It's not your willpower. Our members' real burn averaged ~500 Cal/day below what calculators told them — so the plan was wrong from day one.",
    featRecognizerTitle: 'We measure your real number.',
    featRecognizerBodyLoop:
      'Snap a photo of each meal — no app, no counting (90%+ accurate in our tests). In 2 weeks we know what your body actually burns.',
    featRecognizerBodySteady:
      'Snap a photo of each meal — no app, no counting (90%+ accurate in our tests) — so you see what you really eat.',
    featNutritionistTitle: 'Stuck? A real nutritionist steps in.',
    featNutritionistBody:
      "Not just AI — a human nutritionist finds what's holding you back and gives you one specific fix.",
    featResultsTitle: (amount: string) => `Members lose a median of ${amount} a month.*`,
    featResultsBody: 'No shots, no pills — just the food you already eat.',
    claimBtn: 'Start my plan →',
    claimBtnWhatsapp: 'Start my plan on WhatsApp →',
    fineprintClaim: 'Nothing to download — just text.',
    resultsFootnote: '*Active members who log their meals, Jul–Sep 2026. Individual results vary.',
    convoUserPhoto: '📷 photo of lunch',
    convoCoachPhoto: 'Chicken burrito bowl · ~640 cal · 42g protein — logged. 1,180 left today.',
    email: {
      placeholder: 'you@example.com',
      submit: 'Send me the plan',
      sending: 'Sending…',
      fineprint: 'We email once with your plan. No marketing, no spam.',
      errNoContext: 'No conversation context — refresh and try again.',
      errInvalid: "That doesn't look like a valid email.",
      errFailed: 'submit failed',
      confirmHeadline: '✅ Your plan is on the way.',
    },
  },
  nav: {
    ariaPrimary: 'Primary',
    links: [
      { label: 'How it works', href: 'https://nanorhino.com/#how-it-works' },
      { label: 'Why it works', href: 'https://nanorhino.com/#why' },
      { label: 'Pricing', href: 'https://nanorhino.com/pricing' },
      { label: 'Stories', href: 'https://nanorhino.com/results' },
      { label: 'Message coach', href: 'https://nanorhino.com/message' },
      { label: 'Blog', href: 'https://nanorhino.com/blog' },
      { label: 'About', href: 'https://nanorhino.com/about' },
    ],
    coach: 'Text START',
    coachAria: 'Text START to NanoRhino at (832) 662-5181',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    legal: [
      { label: 'Privacy Policy', href: 'https://nanorhino.com/privacy' },
      { label: 'Terms of Service', href: 'https://nanorhino.com/terms' },
      { label: 'Cookie Policy', href: 'https://nanorhino.com/cookies' },
      { label: 'Support', href: 'https://nanorhino.com/support' },
    ],
  },
  footer: {
    tagline: 'AI nutrition coach in your messaging app. Built by Link Heart Limited in Houston, Texas.',
    productHeading: 'Product',
    companyHeading: 'Company',
    legalHeading: 'Legal',
    product: [
      { label: 'Free TDEE Calculator', href: 'https://nanorhino.com/tdee' },
      { label: 'GLP-1 Calorie & Protein Calculator', href: 'https://nanorhino.com/tdee/glp1-calculator' },
      { label: 'How It Works', href: 'https://nanorhino.com/#how-it-works' },
      { label: 'Why It Works', href: 'https://nanorhino.com/#why' },
      { label: 'Pricing', href: 'https://nanorhino.com/pricing' },
      { label: 'Message your coach', href: 'https://nanorhino.com/message' },
      { label: 'Stories', href: 'https://nanorhino.com/results' },
      { label: 'Compare', href: 'https://nanorhino.com/compare' },
      { label: 'EatPilot', href: 'https://eatpilot.app/' },
    ],
    company: [
      { label: 'About Link Heart Limited', href: 'https://nanorhino.com/about' },
      { label: 'Blog', href: 'https://nanorhino.com/blog' },
    ],
    legal: [
      { label: 'Privacy Policy', href: 'https://nanorhino.com/privacy' },
      { label: 'Terms of Service', href: 'https://nanorhino.com/terms' },
      { label: 'Cookie Policy', href: 'https://nanorhino.com/cookies' },
      { label: 'Support', href: 'https://nanorhino.com/support' },
    ],
    copyright: (year: number) => `© ${year} Link Heart Limited. All rights reserved.`,
  },
  rangeBar: (low, high, marker) =>
    `TDEE range ${low} to ${high} kcal per day, recommended ${marker}`,
}
